import { getApiUrl } from "@/utils/api";
import { useState, useEffect, useRef, useCallback } from "react";
import { useCmsFieldVisibility } from "@/hooks/useCmsFieldVisibility";
import { useShowDbFieldNames } from "@/hooks/useShowDbFieldNames";
import { useSessionHeartbeat } from "@/hooks/useSessionHeartbeat";
import { useAgentSessionTimeout } from "@/hooks/useAgentSessionTimeout";
import { SessionTimeoutWarningModal } from "@/components/property-admin/SessionTimeoutWarningModal";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import StatisticsTab from "@/components/property-admin/StatisticsTab";
import PropertyAdminInfoTab from "@/components/property-admin/PropertyAdminInfoTab";
import PropertyAdminOffersTab from "@/components/property-admin/PropertyAdminOffersTab";
import PropertyAdminOffersReceivedTab from "@/components/property-admin/PropertyAdminOffersReceivedTab";
import DocumentsSection from "@/components/property-admin/DocumentsSection";
import VerifyIdSection from "@/components/property-admin/VerifyIdSection";
import AgentAccountPanel from "@/components/property-admin/AgentAccountPanel";
import AgentOrdersTab from "@/components/property-admin/AgentOrdersTab";
import BuyProductsTab from "@/components/property-admin/BuyProductsTab";
import { PropertyCardsGrid } from "@/components/admin/PropertyCardsGrid";
import { PropertyEditDialog } from "@/components/admin/PropertyEditDialog";
import { 
  LogOut, 
  Save, 
  Home, 
  Loader2,
  Building2,
  MapPin,
  Bed,
  Bath,
  Video,
  Image as ImageIcon,
  ExternalLink,
  Download,
  Users,
  Mail,
  Phone,
  Plus,
  Send,
  Calendar,
  User,
  UserCircle,
  Pencil,
  Trash2,
  MoreVertical,
  Search,
  Cloud,
  CloudOff,
  BarChart3,
  Eye,
  EyeOff,
  TrendingUp,
  MousePointer,
  Clock,
  Share2,
  ShoppingBag,
  ShoppingCart,
  CheckCircle2,
  Printer,
  Upload,
  Heart,
  Check,
  Info as InfoIcon,
  Link2,
  Link2Off,
  HandCoins,
  Inbox as InboxIcon,
  FolderOpen,
  LayoutDashboard,
  Landmark,
  FileCode2,
  Gavel,
  ShieldCheck,
  ShieldAlert,
  Layers,
} from "lucide-react";
import HousePricesTool from "@/components/property-admin/HousePricesTool";
import PlansSection from "@/components/property-admin/PlansSection";
import PlanningApplicationsTool from "@/components/property-admin/PlanningApplicationsTool";
import PlanningAppealsTool from "@/components/property-admin/PlanningAppealsTool";
import { useFavourites } from "@/hooks/useFavourites";
import { resolveImageUrl } from "@/utils/imageUrl";
import { useSiteStyles } from "@/hooks/useSiteStyles";
import axios from "axios";

const API = getApiUrl();
const LOGO_URL = "https://customer-assets.emergentagent.com/job_realty-gallery/artifacts/zzc7uqc2_Screenshot_20260119_035617_Chrome.ico";

// Debounce hook for auto-save
function useDebounce(callback, delay) {
  const timeoutRef = useRef(null);
  
  const debouncedCallback = useCallback((...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  }, [callback, delay]);
  
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  
  return debouncedCallback;
}

/**
 * Property-admin "Favourites" tab — mirrors the public /my-favourites list
 * (localStorage for anonymous, server-backed for signed-in shoppers) so the
 * agent can browse their own bookmarked properties from inside the dashboard
 * without leaving the admin experience.
 */
function PropertyAdminFavouritesTab({ linkedProperties = [] }) {
  const { items, loading, toggle } = useFavourites();
  const mineIds = new Set(linkedProperties.map((p) => p.property_id));
  return (
    <div className="bg-white border border-border p-6" data-testid="favourites-tab-content">
      <h2 className="text-xl font-semibold flex items-center gap-2 mb-1">
        <Heart className="h-5 w-5 fill-red-500 text-red-500" /> Favourites
      </h2>
      <p className="text-sm text-muted-foreground mb-6">
        Properties you&apos;ve bookmarked from the public site. Available for a
        side-by-side comparison via the compare tray.
      </p>
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg p-8 text-center text-sm text-muted-foreground bg-muted/20" data-testid="favourites-empty">
          You haven&apos;t bookmarked any properties yet.
          {" "}
          <Link to="/properties" className="text-primary hover:underline font-medium">Browse homes →</Link>
        </div>
      ) : (
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((f) => {
            const p = f.property || {};
            const img = (p.gallery || [])[0]?.media_url || "";
            const mine = mineIds.has(f.property_id);
            return (
              <li key={f.property_id} className="border border-border rounded-lg overflow-hidden" data-testid={`pa-favourite-${f.property_id}`}>
                <Link to={`/property/${f.property_id}`}>
                  <div className="relative h-32 bg-cover bg-center bg-muted" style={img ? { backgroundImage: `url(${img})` } : {}}>
                    {mine && (
                      <span
                        className="absolute top-2 left-2 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded bg-primary text-primary-foreground shadow"
                        data-testid={`pa-favourite-mine-${f.property_id}`}
                      >
                        Your listing
                      </span>
                    )}
                  </div>
                </Link>
                <div className="p-3">
                  <div className="font-medium text-sm">{p.title || "Home"}</div>
                  <div className="text-xs text-muted-foreground">{p.display_address || ""}</div>
                  <div className="mt-2 flex items-center justify-between">
                    <div className="text-sm font-semibold">
                      {p.price ? `${p.currency || "£"}${Number(p.price).toLocaleString()}` : "POA"}
                    </div>
                    <button
                      type="button"
                      onClick={() => toggle(f.property_id)}
                      className="text-red-600 hover:bg-red-50 p-1.5 rounded"
                      aria-label="Remove"
                      data-testid={`pa-favourite-remove-${f.property_id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default function PropertyAdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  useSessionHeartbeat({ onExpired: () => navigate("/property-admin/login", { replace: true }) });
  const handleInactivityExpire = useCallback(async () => {
    const cookieConsent = localStorage.getItem("cookie_consent");
    try {
      await axios.post(`${API}/property-admin/logout`, {}, { withCredentials: true });
    } catch {
      // Best-effort — the session is already past its inactivity window
      // server-side, so clearing local state and redirecting is enough.
    }
    localStorage.clear();
    sessionStorage.clear();
    if (cookieConsent) localStorage.setItem("cookie_consent", cookieConsent);
    toast.error("You've been logged out due to inactivity. Please log in again.");
    navigate("/property-admin/login", { replace: true });
  }, [navigate]);
  const { showWarning: showInactivityWarning, secondsLeft: inactivitySecondsLeft, stayLoggedIn } = useAgentSessionTimeout({ onExpire: handleInactivityExpire });
  const requestedPropertyId = location.state?.requestedPropertyId;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [propertyData, setPropertyData] = useState(null);
  // Agents with zero properties can still open their private document library
  // and Verify Photo module — this flag flips the empty-state card into a
  // minimal docs-only shell so those property-independent features stay
  // reachable without needing a listing first.
  const [showDocsOnlyShell, setShowDocsOnlyShell] = useState(false);
  const [username, setUsername] = useState("");
  const [loginType, setLoginType] = useState("property_admin");
  const [agentInfo, setAgentInfo] = useState(null);
  // Standalone "Verify ID" — a global per-agent action (not property-scoped),
  // surfaced as a top-level button next to the Selling/Buying section pills.
  const [showGlobalVerifyId, setShowGlobalVerifyId] = useState(false);
  const [linkedProperties, setLinkedProperties] = useState([]);
  const [switchingProperty, setSwitchingProperty] = useState(false);
  const isInitialLoad = useRef(true);
  
  // State for AI rewrite
  const [aiRewriting, setAiRewriting] = useState(false);
  
  // Check if current domain matches company domain (enables email features)
  const { styles: siteStylesData } = useSiteStyles();
  const buyingSectionEnabled = siteStylesData?.buyingSectionEnabled !== false;
  const housePricesSectionEnabled = siteStylesData?.housePricesSectionEnabled !== false;
  const planningAppsSectionEnabled = siteStylesData?.planningAppsSectionEnabled !== false;
  const planningAppealsSectionEnabled = siteStylesData?.planningAppealsSectionEnabled !== false;
  const plansSectionEnabled = siteStylesData?.plansSectionEnabled !== false;
  const addPropertyEnabled = siteStylesData?.addPropertyEnabled !== false;
  const isLiveDomain = (() => {
    const companyDomain = (siteStylesData?.companyDomain || "").toLowerCase().replace(/^www\./, "");
    const currentDomain = window.location.hostname.toLowerCase().replace(/^www\./, "");
    return companyDomain && currentDomain === companyDomain;
  })();
  
  // State for Import URL / Scrape
  const [scrapeUrl, setScrapeUrl] = useState("");
  const [scraping, setScraping] = useState(false);
  const [scrapeResult, setScrapeResult] = useState(null);
  const [showScrapeDialog, setShowScrapeDialog] = useState(false);
  
  // Account / Profile state — agents can self-edit their own agent doc
  const [profileForm, setProfileForm] = useState({
    name: "", logo: "", contact_name: "", email: "", phone: "", whatsapp_phone: "",
    whatsapp_enabled: true,
    show_phone_in_modal: true,
    show_whatsapp_in_modal: true,
    website: "", address: "", notes: "",
    email_notifications: true,
    marketing_emails: true,
    supplier_emails: true,
  });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileHasChanges, setProfileHasChanges] = useState(false);
  const [profileLastSaved, setProfileLastSaved] = useState(null);
  const profileInitialLoad = useRef(true);
  // CMS-field visibility for the property-admin (user) dashboard. Fields that
  // the master admin marks `admin`-only or `hidden` are filtered out below.
  const vis = useCmsFieldVisibility();
  
  // State for inquiries
  const [enquiries, setEnquiries] = useState([]);
  const [loadingEnquiries, setLoadingEnquiries] = useState(false);
  const [showAddEnquiryDialog, setShowAddEnquiryDialog] = useState(false);
  const [showEditEnquiryDialog, setShowEditEnquiryDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [newEnquiry, setNewEnquiry] = useState({ name: "", email: "", phone: "", message: "" });
  const [editingEnquiry, setEditingEnquiry] = useState(null);
  const [addingEnquiry, setAddingEnquiry] = useState(false);
  const [updatingEnquiry, setUpdatingEnquiry] = useState(false);
  const [deletingEnquiry, setDeletingEnquiry] = useState(null);
  const [sendingBrochure, setSendingBrochure] = useState(null);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  // Inline "Reply + mark replied" dialog state — surfaces a pre-filled
  // template the agent can tweak before sending so both sides of the loop
  // (Buying Enquiry History status, receiving agent's reply history) update
  // in one click.
  const [replyDialog, setReplyDialog] = useState({ open: false, enquiry: null, subject: "", message: "", sending: false });
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const stored = window.localStorage.getItem("slh.pa.tab") || "my-properties";
      // "milestones-dashboard" and "details" no longer open as their own
      // tab/modal — milestones expand inline, and the Edit Property Details
      // form is now embedded directly under "my-properties" (see
      // PropertyCardsGrid.jsx / PropertyEditDialog.jsx) — so a stale value
      // from before that change must not strand the agent on a tab with
      // nothing to render.
      return (stored === "milestones-dashboard" || stored === "details") ? "my-properties" : stored;
    } catch { return "my-properties"; }
  });
  useEffect(() => {
    try { window.localStorage.setItem("slh.pa.tab", activeTab); } catch { /* ignore quota */ }
  }, [activeTab]);

  // Debug: `?show=yes` in the URL prints each DB field name next to its
  // label across the property edit form. Walks the embedded Edit Property
  // Details form (data-testid="property-edit-dialog") the same way the
  // master admin dashboard does for its property dialog.
  const showDbNames = useShowDbFieldNames();
  useEffect(() => {
    if (!showDbNames) return;
    if (activeTab !== "my-properties") return;
    const applyBadges = () => {
      const form = document.querySelector('[data-testid="property-edit-dialog"]');
      if (!form) return;
      const inputs = form.querySelectorAll("input[name], textarea[name], select[name]");
      inputs.forEach((el) => {
        const nm = el.getAttribute("name");
        if (!nm) return;
        let label = null;
        let node = el.parentElement;
        for (let hop = 0; hop < 3 && node && !label; hop += 1) {
          label = node.querySelector("label");
          node = node.parentElement;
        }
        if (!label || label.dataset.dbBadgeShown) return;
        const badge = document.createElement("span");
        badge.className =
          "ml-2 inline-block text-[10px] font-mono text-blue-700 bg-blue-50 border border-blue-200 rounded px-1 align-middle";
        badge.textContent = nm;
        badge.setAttribute("data-testid", `db-field-badge-${nm}`);
        label.appendChild(badge);
        label.dataset.dbBadgeShown = "1";
      });
    };
    applyBadges();
    const t = setTimeout(applyBadges, 250);
    return () => clearTimeout(t);
  }, [showDbNames, activeTab]);

  // Section grouping — "Selling" (agent-facing) or "Buying" (shopper-facing).
  // Persisted in localStorage so agents return to the section they last used.
  const [activeSection, setActiveSection] = useState(() => {
    try { return window.localStorage.getItem("slh.pa.section") || "selling"; }
    catch { return "selling"; }
  });
  useEffect(() => {
    try { window.localStorage.setItem("slh.pa.section", activeSection); } catch { /* ignore quota */ }
    // Keep activeTab in sync when the account pill is selected — on reload
    // activeSection may hydrate from storage as "account" while activeTab
    // still points at a section-scoped tab, which would leave the content
    // area blank. Coerce here so the account panel always renders.
    if (activeSection === "account" && activeTab !== "account") {
      setActiveTab("account");
    }
    // If master admin disables the Buying section while an agent is on it
    // (or has it persisted from localStorage), fall back to Selling.
    if (activeSection === "buying" && !buyingSectionEnabled) {
      setActiveSection("selling");
      setActiveTab("my-properties");
    }
    if (activeSection === "house-prices" && !housePricesSectionEnabled) {
      setActiveSection("selling");
      setActiveTab("my-properties");
    }
    if (activeSection === "planning-applications" && !planningAppsSectionEnabled) {
      setActiveSection("selling");
      setActiveTab("my-properties");
    }
    if (activeSection === "planning-appeals" && !planningAppealsSectionEnabled) {
      setActiveSection("selling");
      setActiveTab("my-properties");
    }
    if (activeSection === "plans" && !plansSectionEnabled) {
      setActiveSection("selling");
      setActiveTab("my-properties");
    }
  }, [activeSection, activeTab, buyingSectionEnabled, housePricesSectionEnabled, planningAppsSectionEnabled, planningAppealsSectionEnabled, plansSectionEnabled]);
  const [statistics, setStatistics] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [showNewPropertyDialog, setShowNewPropertyDialog] = useState(false);
  const [newPropertyForm, setNewPropertyForm] = useState({ title: "", price: "", currency: "£", display_address: "", beds: "", baths: "", property_type: "", tenure: "" });
  const [newPropertySaving, setNewPropertySaving] = useState(false);
  const [unlinkingGoogle, setUnlinkingGoogle] = useState(false);
  // Orders tab — fetched lazily when the tab first opens.
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersLoaded, setOrdersLoaded] = useState(false);
  // Buying Enquiry History (outbound) — enquiries the current agent has
  // *sent* to other agents' property pages.
  const [sentEnquiries, setSentEnquiries] = useState([]);
  const [sentEnquiriesLoading, setSentEnquiriesLoading] = useState(false);
  const [sentEnquiriesLoaded, setSentEnquiriesLoaded] = useState(false);
  // Offers received summary — used by My Properties to badge each card with
  // its current offer count/status. Kept at the dashboard level so both
  // My Properties and Offers Received share the same source of truth.
  const [receivedOffers, setReceivedOffers] = useState([]);
  const [receivedOffersLoaded, setReceivedOffersLoaded] = useState(false);
  // When the user clicks a property card's offer badge we jump to the
  // Offers Received tab and highlight that property's group.
  const [focusOffersPropertyId, setFocusOffersPropertyId] = useState(null);

  // Property card action CTAs (Enquiries / Orders / Info / Offers Received /
  // Documents) — each opens the relevant tab body inside a modal, scoped to
  // that one property, instead of navigating the sidebar.
  const [cardModalMode, setCardModalMode] = useState(false);
  const [cardModalPropertyId, setCardModalPropertyId] = useState(null);
  // Which CTA sub-modal is open (null when none). Deliberately separate from
  // `activeTab` so opening one of these from inside the Edit Home Details
  // section never unmounts the My Properties page underneath it.
  const [subModalTab, setSubModalTab] = useState(null);
  const openPropertyModal = async (prop, tab) => {
    if (prop.property_id !== propertyData?.property_id) {
      await handleSwitchProperty(prop.property_id);
    }
    setCardModalPropertyId(prop.property_id);
    setCardModalMode(true);
    setSubModalTab(tab);
  };
  const openEditPortalSubModal = (tab) => {
    openPropertyModal(propertyData, tab);
  };
  const closeCardModal = () => {
    setCardModalMode(false);
    setCardModalPropertyId(null);
    setSubModalTab(null);
    // Badges (Dashboard %, Orders/Enquiries/Documents counts) may be stale
    // after editing inside the modal — refresh them on close.
    refreshMilestonesSummary();
  };

  const refreshReceivedOffers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/property-admin/offers/received`, {
        withCredentials: true,
      });
      setReceivedOffers(data?.offers || []);
      setReceivedOffersLoaded(true);
    } catch {
      // Non-fatal — leave the badge hidden if we can't fetch.
    }
  }, []);

  useEffect(() => {
    if (loginType === "agent" && !receivedOffersLoaded) {
      refreshReceivedOffers();
    }
  }, [loginType, receivedOffersLoaded, refreshReceivedOffers]);

  // Milestone completion % badge on each property card (Dashboard CTA).
  const [milestonesSummary, setMilestonesSummary] = useState({});
  const refreshMilestonesSummary = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/property-milestones/summary`, { withCredentials: true });
      setMilestonesSummary(data?.summaries || {});
    } catch {
      // Non-fatal — badge just stays hidden.
    }
  }, []);
  useEffect(() => {
    if (loginType === "agent") refreshMilestonesSummary();
  }, [loginType, refreshMilestonesSummary]);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleConnectGoogle = () => {
    const redirectUrl = `${window.location.origin}/property-admin/dashboard?google_link=1`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleUnlinkGoogle = async () => {
    setUnlinkingGoogle(true);
    try {
      await axios.post(`${API}/property-admin/google-unlink`, {}, { withCredentials: true });
      setAgentInfo((prev) => {
        if (!prev) return prev;
        const next = { ...prev };
        delete next.google_email;
        delete next.google_user_id;
        delete next.google_name;
        delete next.google_picture;
        delete next.google_linked_at;
        return next;
      });
      toast.success("Google account unlinked");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to unlink Google account");
    } finally {
      setUnlinkingGoogle(false);
    }
  };

  const authCallbackRanRef = useRef(false);
  const stripeReturnHandledRef = useRef(null);
  useEffect(() => {
    if (authCallbackRanRef.current) return;
    authCallbackRanRef.current = true;
    handleAuthCallbackThenLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Process Emergent Google OAuth callback (#session_id=...) before loading dashboard data
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleAuthCallbackThenLoad = async () => {
    const hash = typeof window !== "undefined" ? window.location.hash : "";
    const sessionMatch = hash.match(/session_id=([^&]+)/);
    const sessionId = sessionMatch ? decodeURIComponent(sessionMatch[1]) : null;
    // After Google SSO completes, route based on whether this is a fresh registration
    // (→ Account tab) or a returning sign-in (→ My Properties tab).
    let postSsoTab = "my-properties";
    // Populated from `?next=` when a gated CMS page sent the visitor here.
    let nextPath = null;

    // Handle Facebook OAuth callback redirect (set by /api/facebook/oauth/callback).
    // We land the user directly on the Social Media tab and surface a toast so they
    // immediately see whether the connection succeeded.
    const fbParams = new URLSearchParams(window.location.search);
    if (fbParams.get("fb_connected")) {
      toast.success("Connected to Facebook successfully", { duration: 5000 });
      postSsoTab = "social-media";
      // Strip the query so a refresh doesn't re-toast
      const cleanQuery = new URLSearchParams(window.location.search);
      cleanQuery.delete("fb_connected");
      const newSearch = cleanQuery.toString();
      window.history.replaceState({}, document.title, window.location.pathname + (newSearch ? `?${newSearch}` : ""));
    } else if (fbParams.get("fb_error")) {
      const errMsg = decodeURIComponent(fbParams.get("fb_error"));
      toast.error(`Facebook connection failed: ${errMsg}`, { duration: 7000 });
      postSsoTab = "social-media";
      const cleanQuery = new URLSearchParams(window.location.search);
      cleanQuery.delete("fb_error");
      const newSearch = cleanQuery.toString();
      window.history.replaceState({}, document.title, window.location.pathname + (newSearch ? `?${newSearch}` : ""));
    }

    if (sessionId) {
      const params = new URLSearchParams(window.location.search);
      const isLink = params.get("google_link") === "1";
      nextPath = params.get("next");
      // Clear the session_id from the URL immediately so it can't be re-used
      window.history.replaceState({}, document.title, window.location.pathname);
      // Consume the "came from the dedicated login page" flag right away —
      // win or lose, it must not leak into a later sign-in attempt started
      // from the home page's modal in the same tab.
      let loginEntry = null;
      try { loginEntry = sessionStorage.getItem("slh_agent_login_entry"); } catch { /* storage unavailable */ }
      try { sessionStorage.removeItem("slh_agent_login_entry"); } catch { /* storage unavailable */ }
      try {
        if (isLink) {
          await axios.post(
            `${API}/property-admin/google-link`,
            { session_id: sessionId },
            { withCredentials: true }
          );
          toast.success("Google account linked");
        } else {
          const resp = await axios.post(
            `${API}/property-admin/google-login`,
            { session_id: sessionId },
            { withCredentials: true }
          );
          if (resp.data?.is_new_account) {
            const welcomeName = resp.data?.agent?.google_name || resp.data?.agent?.name || resp.data?.agent?.email || "there";
            toast.success(`Welcome, ${welcomeName}! Your customer account has been created.`, { duration: 6000 });
            // Fresh registration — land on Account so the new agent can complete their profile
            postSsoTab = "account";
          } else {
            toast.success("Signed in with Google");
            // Returning agent — land on My Properties
            postSsoTab = "my-properties";
          }
        }
      } catch (err) {
        const detail = err.response?.data?.detail;
        const msg = typeof detail === "string" ? detail : "Google sign-in failed";
        if (!isLink) {
          // Send disabled-account (and other) sign-in failures back to the
          // dedicated agent login page when that's where the attempt
          // started, instead of the public home page's sign-in modal.
          if (loginEntry === "property_admin_login") {
            // The red banner on that page already shows this message —
            // skip the toast to avoid showing the error twice.
            navigate("/property-admin/login?login_error=" + encodeURIComponent(msg), { replace: true });
          } else {
            toast.error(msg);
            navigate("/?open_signin=1&google_error=" + encodeURIComponent(msg), { replace: true });
          }
          return;
        }
        toast.error(msg);
      }
    }
    // Only override the activeTab when a real callback (SSO or FB) was
    // processed on this mount — otherwise a plain reload would clobber the
    // localStorage-persisted tab.
    const cameFromCallback = Boolean(
      sessionId || fbParams.get("fb_connected") || fbParams.get("fb_error")
    );
    if (cameFromCallback) {
      // Signed in via a gated CMS page redirect — send them back to the
      // exact page they wanted instead of showing the dashboard tabs.
      if (sessionId && nextPath && nextPath.startsWith("/") && !nextPath.startsWith("/property-admin")) {
        navigate(nextPath, { replace: true });
        return;
      }
      setActiveTab(postSsoTab);
    }
    await fetchPropertyData();
  };

  useEffect(() => {
    if (propertyData && activeTab === "enquiries") {
      fetchEnquiries();
    }
    if (propertyData && activeTab === "statistics") {
      fetchStatistics();
    }
  }, [propertyData, activeTab]);

  // Load orders once up front (not just when the Orders modal opens) so the
  // per-property order count badge on My Properties has data immediately.
  useEffect(() => {
    if (ordersLoaded) return;
    let alive = true;
    setOrdersLoading(true);
    axios.get(`${API}/property-admin/me/orders`, { withCredentials: true })
      .then(({ data }) => { if (alive) setOrders(data.orders || []); })
      .catch(() => { if (alive) setOrders([]); })
      .finally(() => { if (!alive) return; setOrdersLoading(false); setOrdersLoaded(true); });
    return () => { alive = false; };
  }, [ordersLoaded]);

  const reloadOrders = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API}/property-admin/me/orders`,
        { withCredentials: true },
      );
      setOrders(data.orders || []);
    } catch { /* ignore */ }
  }, []);

  // After a "Buy Products" Stripe checkout, the agent lands back here (My
  // Properties) instead of the public payment-success page. Poll once the
  // payment clears, then refresh property + order data for the new badges.
  useEffect(() => {
    const sessionId = new URLSearchParams(location.search).get("stripe_session_id");
    if (!sessionId) return;
    if (stripeReturnHandledRef.current === sessionId) return;
    stripeReturnHandledRef.current = sessionId;
    navigate(location.pathname, { replace: true });
    // Returning from Stripe is a full page reload — `activeTab` restores
    // from localStorage as stale "buy-products", but `cardModalPropertyId`
    // isn't persisted and resets to null, leaving that modal stuck on its
    // loading spinner forever. Force a clean landing on My Properties.
    closeCardModal();
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          // A Plans purchase has no property context (unlike the per-listing
          // Buy Products flow) — land on the Plans tab with its own message
          // instead of forcing "My Properties".
          const isPlanPurchase = !data.order?.assigned_property_id;
          if (isPlanPurchase) {
            toast.success("Payment received — plan activated");
            setActiveSection("plans");
            setActiveTab("plans");
            return;
          }
          toast.success("Payment received — product added to your property");
          fetchPropertyData();
          reloadOrders();
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          toast.error("Payment was not completed");
          return;
        }
      } catch { /* keep polling */ }
      if (attempts < 8) setTimeout(poll, 1500);
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Lazy-load the outbound Buying Enquiry History when its tab opens.
  useEffect(() => {
    if (activeTab !== "sent-enquiries" || sentEnquiriesLoaded) return;
    let alive = true;
    setSentEnquiriesLoading(true);
    axios.get(`${API}/property-admin/me/sent-enquiries`, { withCredentials: true })
      .then(({ data }) => { if (alive) setSentEnquiries(data.enquiries || []); })
      .catch(() => { if (alive) setSentEnquiries([]); })
      .finally(() => { if (!alive) return; setSentEnquiriesLoading(false); setSentEnquiriesLoaded(true); });
    return () => { alive = false; };
  }, [activeTab, sentEnquiriesLoaded]);

  const fetchPropertyData = async () => {
    try {
      const response = await axios.get(`${API}/property-admin/me`, { withCredentials: true });
      setPropertyData(response.data.property);
      setUsername(response.data.username);
      setLoginType(response.data.login_type || "property_admin");
      
      // If logged in as agent, store agent info and linked properties
      if (response.data.agent) {
        setAgentInfo(response.data.agent);
        // Pre-fill the profile editing form so agents can self-edit
        const a = response.data.agent;
        setProfileForm({
          name: a.name || "",
          logo: a.logo || "",
          contact_name: a.contact_name || "",
          email: a.email || "",
          phone: a.phone || "",
          whatsapp_phone: a.whatsapp_phone || "",
          whatsapp_enabled: a.whatsapp_enabled !== false,
          show_phone_in_modal: a.show_phone_in_modal !== false,
          show_whatsapp_in_modal: a.show_whatsapp_in_modal !== false,
          website: a.website || "",
          address: a.address || "",
          notes: a.notes || "",
          email_notifications: a.email_notifications !== false,
          marketing_emails: a.marketing_emails !== false,
          supplier_emails: a.supplier_emails !== false,
        });
      }
      if (response.data.linked_properties) {
        setLinkedProperties(response.data.linked_properties);
        
        // Auto-switch to requested property ONLY on initial load
        if (!initialLoadComplete && requestedPropertyId && response.data.property?.property_id !== requestedPropertyId) {
          const hasAccess = response.data.linked_properties.some(p => p.property_id === requestedPropertyId);
          if (hasAccess) {
            // Switch to the requested property
            try {
              await axios.post(`${API}/property-admin/switch-property/${requestedPropertyId}`, {}, { withCredentials: true });
              // Refetch to get the correct property data
              const updatedResponse = await axios.get(`${API}/property-admin/me`, { withCredentials: true });
              setPropertyData(updatedResponse.data.property);
              toast.success(`Switched to requested property`);
            } catch (switchError) {
              console.error("Failed to auto-switch property:", switchError);
            }
          }
        }
      }
      setInitialLoadComplete(true);
      return response.data;
    } catch (error) {
      const status = error.response?.status;
      console.error(`Failed to fetch property (status=${status}):`, error.response?.data || error.message);
      // Only bounce on real auth failures — to the sign-in page (not the
      // public home page) so the user can immediately sign back in, same
      // as useSessionHeartbeat/useAgentSessionTimeout already do.
      // For network/server errors stay on the dashboard so the user sees the error.
      if (status === 401 || status === 403) {
        navigate("/property-admin/login", { replace: true });
      } else {
        toast.error(`Failed to load dashboard${status ? ` (${status})` : ""}`);
      }
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchProperty = async (propertyId) => {
    console.log("Switching to property:", propertyId);
    console.log("Current property:", propertyData?.property_id);
    
    if (propertyId === propertyData?.property_id) {
      console.log("Same property, skipping switch");
      return;
    }
    
    setSwitchingProperty(true);
    try {
      console.log("Making switch API call...");
      const switchResponse = await axios.post(`${API}/property-admin/switch-property/${propertyId}`, {}, { withCredentials: true });
      console.log("Switch API response:", switchResponse.data);
      
      // Fetch the updated property data directly
      console.log("Fetching updated property data...");
      const response = await axios.get(`${API}/property-admin/me`, { withCredentials: true });
      console.log("Fetched property data:", response.data.property?.property_id, response.data.property?.title);
      
      setPropertyData(response.data.property);
      setUsername(response.data.username);
      
      // Update linked properties list
      if (response.data.linked_properties) {
        setLinkedProperties(response.data.linked_properties);
      }
      
      toast.success(`Switched to ${response.data.property?.title || 'property'}`);
      setActiveTab("my-properties");
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error("Failed to switch property:", error);
      toast.error(error.response?.data?.detail || "Failed to switch property");
    } finally {
      setSwitchingProperty(false);
    }
  };

  const [deletingProperty, setDeletingProperty] = useState(false);

  const handleDeleteProperty = async (propertyId) => {
    setDeletingProperty(true);
    try {
      await axios.delete(`${API}/property-admin/properties/${propertyId}`, { withCredentials: true });
      toast.success("Property deleted");
      setActiveTab("my-properties");
      await fetchPropertyData();
    } catch (error) {
      console.error("Failed to delete property:", error);
      toast.error(error.response?.data?.detail || "Failed to delete property");
    } finally {
      setDeletingProperty(false);
    }
  };

  const fetchEnquiries = async () => {
    setLoadingEnquiries(true);
    try {
      const response = await axios.get(`${API}/property-admin/enquiries`, { withCredentials: true });
      setEnquiries(response.data);
    } catch (error) {
      console.error("Failed to fetch enquiries:", error);
      toast.error("Failed to load enquiries");
    } finally {
      setLoadingEnquiries(false);
    }
  };

  const fetchStatistics = async () => {
    setStatsLoading(true);
    try {
      const response = await axios.get(`${API}/property-admin/statistics`, { withCredentials: true });
      setStatistics(response.data);
    } catch (error) {
      console.error("Failed to fetch statistics:", error);
      // Set default/empty statistics on error
      setStatistics({
        total_views: 0,
        unique_visitors: 0,
        total_enquiries: enquiries.length || 0,
        total_calls: 0,
        avg_time_on_page: 0,
        views_this_week: [],
        views_this_month: 0,
        enquiries_this_month: 0,
        conversion_rate: 0
      });
    } finally {
      setStatsLoading(false);
    }
  };

  const handleAddEnquiry = async () => {
    if (!newEnquiry.name || !newEnquiry.email) {
      toast.error("Name and email are required");
      return;
    }
    
    setAddingEnquiry(true);
    try {
      await axios.post(`${API}/property-admin/enquiries`, newEnquiry, { withCredentials: true });
      toast.success("Enquiry added successfully");
      setShowAddEnquiryDialog(false);
      setNewEnquiry({ name: "", email: "", phone: "", message: "" });
      fetchEnquiries();
    } catch (error) {
      console.error("Failed to add enquiry:", error);
      toast.error("Failed to add enquiry");
    } finally {
      setAddingEnquiry(false);
    }
  };

  const handleEditEnquiry = (enquiry) => {
    setEditingEnquiry({ ...enquiry });
    setShowEditEnquiryDialog(true);
  };

  const handleUpdateEnquiry = async () => {
    if (!editingEnquiry.name || !editingEnquiry.email) {
      toast.error("Name and email are required");
      return;
    }
    
    setUpdatingEnquiry(true);
    try {
      await axios.put(
        `${API}/property-admin/enquiries/${editingEnquiry.enquiry_id}`,
        {
          name: editingEnquiry.name,
          email: editingEnquiry.email,
          phone: editingEnquiry.phone,
          message: editingEnquiry.message
        },
        { withCredentials: true }
      );
      toast.success("Enquiry updated successfully");
      setShowEditEnquiryDialog(false);
      setEditingEnquiry(null);
      fetchEnquiries();
    } catch (error) {
      console.error("Failed to update enquiry:", error);
      toast.error("Failed to update enquiry");
    } finally {
      setUpdatingEnquiry(false);
    }
  };

  const handleDeleteEnquiry = async (enquiryId) => {
    setDeletingEnquiry(enquiryId);
    try {
      await axios.delete(`${API}/property-admin/enquiries/${enquiryId}`, { withCredentials: true });
      toast.success("Enquiry deleted successfully");
      setShowDeleteConfirm(null);
      fetchEnquiries();
    } catch (error) {
      console.error("Failed to delete enquiry:", error);
      toast.error("Failed to delete enquiry");
    } finally {
      setDeletingEnquiry(null);
    }
  };

  const handleSendBrochure = async (enquiry) => {
    setSendingBrochure(enquiry.enquiry_id);
    try {
      await axios.post(`${API}/property-admin/send-brochure`, {
        customer_email: enquiry.email,
        customer_name: enquiry.name
      }, { withCredentials: true });
      toast.success(`Brochure sent to ${enquiry.email}`);
    } catch (error) {
      console.error("Failed to send brochure:", error);
      toast.error(error.response?.data?.detail || "Failed to send brochure");
    } finally {
      setSendingBrochure(null);
    }
  };

  // Open the inline reply dialog with a pre-filled template. The agent
  // name comes from their own account (`agentInfo`) and the property title
  // from the enquiry itself so the email reads naturally without the agent
  // having to type "Hi X" boilerplate.
  const openReplyDialog = (enquiry) => {
    const agentName = agentInfo?.contact_name || agentInfo?.name || "Your contact";
    const propertyTitle = enquiry.property_title || propertyData?.title || "your enquiry";
    const subject = `Re: ${propertyTitle}`;
    const message = `Hi ${enquiry.name || "there"},

Thanks for enquiring about ${propertyTitle}. I'd love to help — happy to answer any questions, arrange a viewing, or share more details.

Let me know what works best.

Best regards,
${agentName}`;
    setReplyDialog({ open: true, enquiry, subject, message, sending: false });
  };

  const handleSendReply = async () => {
    if (!replyDialog.enquiry) return;
    const { subject, message } = replyDialog;
    if (!message?.trim()) {
      toast.error("Please add a message before sending");
      return;
    }
    setReplyDialog((d) => ({ ...d, sending: true }));
    try {
      const { data } = await axios.post(
        `${API}/property-admin/enquiries/${replyDialog.enquiry.enquiry_id}/reply`,
        { subject: subject?.trim() || `Re: ${replyDialog.enquiry.property_title || "your enquiry"}`, message: message.trim() },
        { withCredentials: true },
      );
      if (data.email_sent) {
        toast.success(`Reply sent to ${replyDialog.enquiry.email}`);
      } else {
        toast.warning("Marked as replied — but the email couldn't be sent. Check your Resend integration.");
      }
      setReplyDialog({ open: false, enquiry: null, subject: "", message: "", sending: false });
      fetchEnquiries();
    } catch (error) {
      console.error("Failed to send reply:", error);
      toast.error(error.response?.data?.detail || "Failed to send reply");
      setReplyDialog((d) => ({ ...d, sending: false }));
    }
  };

  const generateBrochure = async () => {
    setPdfGenerating(true);
    try {
      toast.info("Generating PDF brochure...");
      
      // Use the backend endpoint to generate PDF with embedded images
      const response = await axios.get(`${API}/property-admin/generate-pdf`, {
        withCredentials: true,
        responseType: 'blob',
        timeout: 60000  // 60 second timeout for PDF generation
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${propertyData.title.replace(/[^a-z0-9]/gi, '_')}_Brochure.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF brochure downloaded!");
    } catch (error) {
      console.error("PDF generation error:", error);
      toast.error("Failed to generate PDF");
    } finally {
      setPdfGenerating(false);
    }
  };

  const formatEnquiryDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleLogout = async () => {
    // Preserve the cookie-consent choice across logout — clearing it would
    // make the cookie banner pop back up on the homepage right after
    // logging out, even though the visitor already answered it.
    const cookieConsent = localStorage.getItem("cookie_consent");
    const restoreCookieConsent = () => {
      if (cookieConsent) localStorage.setItem("cookie_consent", cookieConsent);
    };
    try {
      // Kill the SSO session on the backend
      await axios.post(`${API}/property-admin/logout`, {}, { withCredentials: true });

      // Clear any local storage or session storage
      localStorage.clear();
      sessionStorage.clear();
      restoreCookieConsent();

      toast.success("Logged out successfully");

      // Full browser navigation (not client-side SPA routing) — guarantees
      // the homepage loads fresh with no leftover dashboard state in memory
      // and the browser can't restore a stale authenticated view via bfcache.
      window.location.href = "/";
    } catch (error) {
      // Even if logout fails, clear local data and redirect
      localStorage.clear();
      sessionStorage.clear();
      restoreCookieConsent();
      window.location.href = "/";
    }
  };

  // AI Rewrite Description
  const handleAIRewrite = async () => {
    if (!propertyData?.description) {
      toast.error("Please enter a description first");
      return;
    }
    
    setAiRewriting(true);
    try {
      const response = await axios.post(
        `${API}/property-admin/ai-rewrite-description`,
        {
          description: propertyData.description,
          property_context: {
            title: propertyData.title,
            display_address: propertyData.display_address,
            price: propertyData.price,
            bedrooms: propertyData.bedrooms,
            bathrooms: propertyData.bathrooms,
            sqft: propertyData.sqft
          }
        },
        { withCredentials: true }
      );
      
      if (response.data?.rewritten_description) {
        setPropertyData(prev => ({ ...prev, description: response.data.rewritten_description }));
        setHasUnsavedChanges(true);
        toast.success("Description rewritten by AI!");
      }
    } catch (error) {
      console.error("AI rewrite error:", error);
      toast.error(error.response?.data?.detail || "Failed to rewrite description");
    } finally {
      setAiRewriting(false);
    }
  };

  // Import from URL (scrape)
  const handleScrape = async () => {
    if (!scrapeUrl) { toast.error("Please enter a URL"); return; }
    setScraping(true);
    setScrapeResult(null);
    try {
      const response = await axios.post(`${API}/property-admin/scrape`, { url: scrapeUrl }, { withCredentials: true });
      setScrapeResult(response.data.data);
      toast.success("Property data imported successfully");
    } catch (error) {
      console.error("Scrape failed:", error);
      toast.error(error.response?.data?.detail || "Failed to import property data");
    } finally {
      setScraping(false);
    }
  };

  const handleProfileFieldChange = (field, value) => {
    setProfileForm((prev) => ({ ...prev, [field]: value }));
    setProfileHasChanges(true);
  };

  // Auto-save for the agent's own Account Profile panel (mirrors the
  // property auto-save pattern above).
  const performProfileAutoSave = useCallback(async (form) => {
    if (!form || !form.name?.trim() || profileSaving) return;
    setProfileSaving(true);
    try {
      const res = await axios.put(`${API}/property-admin/profile`, form, { withCredentials: true });
      setAgentInfo(res.data);
      setProfileLastSaved(new Date());
      setProfileHasChanges(false);
    } catch (error) {
      console.error("Profile auto-save error:", error);
      toast.error(error.response?.data?.detail || "Failed to auto-save profile");
    } finally {
      setProfileSaving(false);
    }
  }, [profileSaving]);

  const debouncedProfileSave = useDebounce(performProfileAutoSave, 1500);

  useEffect(() => {
    if (profileInitialLoad.current) {
      profileInitialLoad.current = false;
      return;
    }
    if (profileHasChanges && profileForm.name?.trim()) {
      debouncedProfileSave(profileForm);
    }
  }, [profileForm, profileHasChanges, debouncedProfileSave]);

  const handleSaveProfile = async () => {
    if (!profileForm.name?.trim()) {
      toast.error("Full name is required");
      return;
    }
    setProfileSaving(true);
    try {
      const res = await axios.put(
        `${API}/property-admin/profile`,
        profileForm,
        { withCredentials: true }
      );
      setAgentInfo(res.data);
      // Re-hydrate the form from what the server actually stored so the UI
      // always mirrors persisted state (defends against silent field drops,
      // whitespace trims, or default backfills on the backend).
      const a = res.data || {};
      setProfileForm({
        name: a.name || "",
        logo: a.logo || "",
        contact_name: a.contact_name || "",
        email: a.email || "",
        phone: a.phone || "",
        whatsapp_phone: a.whatsapp_phone || "",
        whatsapp_enabled: a.whatsapp_enabled !== false,
        show_phone_in_modal: a.show_phone_in_modal !== false,
        show_whatsapp_in_modal: a.show_whatsapp_in_modal !== false,
        website: a.website || "",
        address: a.address || "",
        notes: a.notes || "",
        email_notifications: a.email_notifications !== false,
        marketing_emails: a.marketing_emails !== false,
        supplier_emails: a.supplier_emails !== false,
      });
      setProfileHasChanges(false);
      toast.success("Profile saved");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save profile");
    } finally {
      setProfileSaving(false);
    }
  };

  const applyScrapeData = () => {
    if (!scrapeResult) return;
    const updates = { ...propertyData };
    if (scrapeResult.title) updates.title = scrapeResult.title;
    if (scrapeResult.price) updates.price = String(scrapeResult.price);
    if (scrapeResult.display_address) updates.display_address = scrapeResult.display_address;
    if (scrapeResult.beds) updates.beds = parseInt(scrapeResult.beds);
    if (scrapeResult.baths) updates.baths = parseInt(scrapeResult.baths);
    if (scrapeResult.description) updates.description = scrapeResult.description;
    if (scrapeResult.features) updates.features = scrapeResult.features;
    if (scrapeResult.size) updates.size = scrapeResult.size;
    if (scrapeResult.property_type) updates.property_type = scrapeResult.property_type;
    if (scrapeResult.council_tax_band) updates.council_tax_band = scrapeResult.council_tax_band.replace(/band\s*/i, '').trim();
    if (scrapeResult.parking_spaces) updates.parking_spaces = parseInt(scrapeResult.parking_spaces);
    if (scrapeResult.garden_facing) updates.garden_facing = scrapeResult.garden_facing;
    if (scrapeResult.floorplan_image) updates.floorplan_image = scrapeResult.floorplan_image;
    if (scrapeResult.images?.length > 0) {
      updates.gallery = scrapeResult.images.map((u, i) => ({
        media_url: u,
        media_type: /\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(u) ? 5 : 1,
        caption: "",
        sort_order: i,
      }));
    }
    setPropertyData(updates);
    setHasUnsavedChanges(true);
    const imgCount = scrapeResult.images?.length || 0;
    toast.success(`Data applied${imgCount > 0 ? ` (${imgCount} images)` : ""}${scrapeResult.floorplan_image ? ", floorplan" : ""}`);
    setScrapeResult(null);
    setScrapeUrl("");
    setShowScrapeDialog(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPropertyData(prev => ({ ...prev, [name]: value }));
    setHasUnsavedChanges(true);
  };

  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    setPropertyData(prev => ({ ...prev, [name]: parseInt(value) || 0 }));
    setHasUnsavedChanges(true);
  };

  // Auto-save function. `includeGallery` is only true for an explicit Save
  // click (handleSave) — the silent debounced autosave never touches gallery,
  // since gallery has its own dedicated persistence path (Rendy add/remove
  // endpoints) and a stale local array could otherwise clobber it.
  const performAutoSave = useCallback(async (data, { includeGallery = false } = {}) => {
    if (!data || saving) return;
    
    setSaving(true);
    try {
      const updateData = {
        title: data.title,
        price: data.price,
        display_address: data.display_address,
        street_name: data.street_name,
        cms_town_city: data.cms_town_city,
        cms_House_Name_Number: data.cms_House_Name_Number,
        postcode: data.postcode,
        cms_postcode_1: data.cms_postcode_1,
        cms_postcode_2: data.cms_postcode_2,
        latitude: data.latitude ? parseFloat(data.latitude) : null,
        longitude: data.longitude ? parseFloat(data.longitude) : null,
        beds: data.beds,
        baths: data.baths,
        description: data.description,
        video_url: data.video_url,
        video_type: data.video_type || "youtube",
        show_video_thumbnail: data.show_video_thumbnail,
        featured: data.featured,
        show_header: data.show_header,
        show_footer: data.show_footer,
        show_back_link: data.show_back_link,
        size: data.size,
        property_type: data.property_type,
        tenure: data.tenure,
        floorplan_image: data.floorplan_image,
        floorplan_size: data.floorplan_size,
        features: data.features,
        nearest_stations: data.nearest_stations,
        council_tax_band: data.council_tax_band,
        parking_spaces: data.parking_spaces ? parseInt(data.parking_spaces) : null,
        garden_facing: data.garden_facing,
        date_added: data.date_added,
        // Live status fields
        is_live: data.is_live,
        live_from: data.live_from,
        live_to: data.live_to,
        // Vendor (property owner) contact details
        vendor_name: data.vendor_name,
        vendor_email: data.vendor_email,
        vendor_phone: data.vendor_phone
      };

      if (includeGallery) {
        // Sort gallery by sort_order so main_image reflects the actual visual order.
        const gallery = [...(data.gallery || [])].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        updateData.main_image = gallery.length > 0 ? (gallery[0].media_url || null) : null;
        updateData.gallery = gallery;
      }

      await axios.put(`${API}/property-admin/property`, updateData, { withCredentials: true });
      setLastSaved(new Date());
      setHasUnsavedChanges(false);
      
      // Update linked properties list with new title
      setLinkedProperties(prev => prev.map(p => 
        p.property_id === data.property_id ? { ...p, title: data.title } : p
      ));
    } catch (error) {
      console.error("Auto-save error:", error);
      toast.error("Failed to auto-save changes");
    } finally {
      setSaving(false);
    }
  }, [saving]);

  // Debounced auto-save (1.5 second delay)
  const debouncedSave = useDebounce(performAutoSave, 1500);

  // Trigger auto-save when propertyData changes
  useEffect(() => {
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      return;
    }
    if (propertyData && hasUnsavedChanges) {
      debouncedSave(propertyData);
    }
  }, [propertyData, hasUnsavedChanges, debouncedSave]);

  const handleSave = async () => {
    if (propertyData) {
      await performAutoSave(propertyData, { includeGallery: true });
      toast.success("Changes saved!");
    }
  };

  const handleGalleryUpload = (urls) => {
    setPropertyData(prev => {
      const existing = prev.gallery || [];
      const startIdx = existing.length;
      const isVideo = (u) => /\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(u || "");
      const additions = urls.map((u, i) => ({
        media_url: u,
        media_type: isVideo(u) ? 5 : 1,
        caption: "",
        sort_order: startIdx + i,
      }));
      return { ...prev, gallery: [...existing, ...additions] };
    });
    setHasUnsavedChanges(true);
  };

  const handleRemoveGalleryImage = (index) => {
    setPropertyData(prev => ({
      ...prev,
      gallery: prev.gallery.filter((_, i) => i !== index)
    }));
    setHasUnsavedChanges(true);
  };

  const handleReorderGallery = (newOrder) => {
    setPropertyData(prev => ({
      ...prev,
      gallery: newOrder
    }));
    setHasUnsavedChanges(true);
  };

  const formatPrice = (price, currency = "£") => {
    if (!price && price !== 0) return "Price on Application";
    try {
      return `${currency}${Number(price).toLocaleString()}`;
    } catch {
      return `${currency}${price}`;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!propertyData) {
    // Agent with no properties yet — show empty-state card with a "Create Property" CTA
    if (loginType === "agent") {
      // Docs-only shell so agents can still reach their private document
      // library + Verify Photo module even before their first listing.
      if (showDocsOnlyShell) {
        return (
          <div className="min-h-screen bg-muted/30" data-testid="property-admin-docs-only">
            <header className="bg-white border-b border-border sticky top-0 z-50 shadow-sm">
              <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Building2 className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <h1 className="font-serif text-lg font-medium">{agentInfo?.name || "Customer"}</h1>
                    <p className="text-xs text-muted-foreground">Documents & Verify Photo</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowDocsOnlyShell(false)}
                    className="rounded-none"
                    data-testid="docs-only-back-btn"
                  >
                    Back
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleLogout}
                    className="rounded-none"
                    data-testid="docs-only-logout-btn"
                  >
                    Sign out
                  </Button>
                </div>
              </div>
            </header>
            <main className="max-w-7xl mx-auto p-6">
              <div className="bg-white border border-border p-6" data-testid="documents-tab-content">
                <div className="mb-4">
                  <h2 className="text-xl font-semibold flex items-center gap-2 mb-1">
                    <FolderOpen className="h-5 w-5" /> Documents
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Your private document library. Organise files into nested folders, share them via a public link or email, and protect important folders from accidental deletion.
                  </p>
                </div>
                <DocumentsSection />
              </div>
            </main>
          </div>
        );
      }
      return (
        <div className="min-h-screen bg-muted/30 flex items-center justify-center px-6" data-testid="property-admin-empty-state">
          <div className="max-w-md w-full bg-white border border-border p-8 text-center space-y-5">
            <Building2 className="h-12 w-12 text-muted-foreground mx-auto" />
            <div>
              <h2 className="font-serif text-2xl font-medium">Welcome, {agentInfo?.name || "Customer"}</h2>
              <p className="text-sm text-muted-foreground mt-2">
                You don&apos;t have any properties yet. Create your first listing to get started.
              </p>
            </div>
            {addPropertyEnabled && (
              <Button
                onClick={() => setShowNewPropertyDialog(true)}
                className="rounded-none w-full"
                data-testid="empty-state-new-property-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create First Property
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => setShowDocsOnlyShell(true)}
              className="rounded-none w-full"
              data-testid="empty-state-view-documents-btn"
            >
              <FolderOpen className="h-4 w-4 mr-2" />
              View My Documents
            </Button>
            <button
              onClick={handleLogout}
              className="block w-full text-xs text-muted-foreground hover:text-foreground"
              data-testid="empty-state-logout-btn"
            >
              Sign out
            </button>
          </div>

          {/* New Property Dialog reused */}
          <Dialog open={showNewPropertyDialog} onOpenChange={setShowNewPropertyDialog}>
            <DialogContent className="sm:max-w-lg rounded-none" data-testid="new-property-dialog">
              <DialogHeader>
                  <DialogTitle className="font-serif text-xl">Add New Home</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Title *</Label>
                  <Input
                    value={newPropertyForm.title}
                    onChange={(e) => setNewPropertyForm({ ...newPropertyForm, title: e.target.value })}
                    className="rounded-none mt-1"
                    placeholder="e.g. 123 High Street"
                    data-testid="new-property-title"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowNewPropertyDialog(false)} className="rounded-none">Cancel</Button>
                <Button
                  disabled={!newPropertyForm.title.trim() || newPropertySaving}
                  className="rounded-none"
                  data-testid="create-property-btn"
                  onClick={async () => {
                    setNewPropertySaving(true);
                    try {
                      const res = await axios.post(`${API}/property-admin/new-property`, {
                        title: newPropertyForm.title,
                      }, { withCredentials: true });
                      toast.success(`"${res.data.title}" created`);
                      setShowNewPropertyDialog(false);
                      setNewPropertyForm({ title: "", price: "", currency: "£", display_address: "", beds: "", baths: "", property_type: "", tenure: "" });
                      // Switch into the new property so propertyData populates
                      await handleSwitchProperty(res.data.property_id);
                      await fetchPropertyData();
                    } catch (err) {
                      toast.error(err.response?.data?.detail || "Failed to create property");
                    } finally {
                      setNewPropertySaving(false);
                    }
                  }}
                >
                  {newPropertySaving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating…</> : "Create Property"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      );
    }
    return null;
  }

  return (
    <div className="min-h-screen bg-muted/30" data-testid="property-admin-dashboard">
      <SessionTimeoutWarningModal open={showInactivityWarning} secondsLeft={inactivitySecondsLeft} onStayLoggedIn={stayLoggedIn} />
      {/* Sticky Header with Save */}
      <header className="bg-white border-b border-border sticky top-0 z-50 shadow-sm">
        <div className="max-w-5xl mx-auto px-6 py-3">
          {/* Top Row: Logo, "Property Admin" Heading, Auto-save Status */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Agent Logo or Building Icon */}
              {propertyData.agent_logo ? (
                <img 
                  src={resolveImageUrl(propertyData.agent_logo)} 
                  alt={propertyData.agent_name || "Customer"}
                  className="h-10 w-auto max-w-[80px] object-contain"
                />
              ) : (
                <Building2 className="h-7 w-7 text-primary" />
              )}
              <div>
                <h1 className="font-serif text-lg font-medium" data-testid="property-admin-heading">
                  Property Admin
                </h1>
                <p className="text-xs text-muted-foreground flex items-center gap-2">
                  {propertyData.agent_name && <span>{propertyData.agent_name}</span>}
                  {loginType === "agent" && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                      Customer
                    </span>
                  )}
                </p>
              </div>
            </div>

            {/* Auto-save Status Indicator + Sign-out on the same row */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 text-sm" data-testid="autosave-status">
                {saving ? (
                  <span className="flex items-center gap-1.5 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-xs">Saving...</span>
                  </span>
                ) : hasUnsavedChanges ? (
                  <span className="flex items-center gap-1.5 text-amber-600">
                    <CloudOff className="h-4 w-4" />
                    <span className="text-xs">Unsaved</span>
                  </span>
                ) : lastSaved ? (
                  <span className="flex items-center gap-1.5 text-green-600">
                    <Cloud className="h-4 w-4" />
                    <span className="text-xs">Saved</span>
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5 text-muted-foreground">
                    <Cloud className="h-4 w-4" />
                    <span className="text-xs">Auto-save on</span>
                  </span>
                )}
              </div>
              {/* Sign-out — moved from its own row per admin request. PDF and
                  View Live CTAs were removed. */}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-muted-foreground hover:text-foreground h-8 px-2"
                data-testid="property-admin-logout-btn"
                title="Sign out"
              >
                <LogOut size={16} />
              </Button>
            </div>
          </div>
        </div>
        
        {/* Section pills — top-level split between what the user sells (agent
            workflow), what they buy (shopper workflow), and personal account
            settings. Selecting a section filters the sub-tab bar below to
            the tabs that belong to it. */}
        <div className="max-w-5xl mx-auto px-6 pt-3 pb-1 border-t border-border">
          <div className="flex gap-2 items-center" data-testid="property-admin-sections">
            {[
              { id: "selling", label: "Selling", icon: Building2 },
              ...(buyingSectionEnabled ? [{ id: "buying", label: "Buying", icon: ShoppingBag }] : []),
              ...(housePricesSectionEnabled ? [{ id: "house-prices", label: "House Prices", icon: Landmark }] : []),
              ...(planningAppsSectionEnabled ? [{ id: "planning-applications", label: "Planning Applications", icon: FileCode2 }] : []),
              ...(planningAppealsSectionEnabled ? [{ id: "planning-appeals", label: "Planning Appeals", icon: Gavel }] : []),
              ...(plansSectionEnabled ? [{ id: "plans", label: "Plans", icon: Layers }] : []),
            ].map((s) => {
              const Icon = s.icon;
              const active = activeSection === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => {
                    setActiveSection(s.id);
                    // Jump to the first tab in the newly-selected section so
                    // the sub-tab bar always has an active pill. Buying
                    // defaults to Favourites since My Orders now lives on
                    // the Selling side. House Prices, Planning Applications,
                    // Planning Appeals and Plans have no sub-tabs of their
                    // own — the section id doubles as the tab id.
                    if (s.id === "house-prices" || s.id === "planning-applications" || s.id === "planning-appeals" || s.id === "plans") {
                      setActiveTab(s.id);
                    } else {
                      setActiveTab(s.id === "selling" ? "my-properties" : "favourites");
                    }
                  }}
                  className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    active
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground hover:bg-muted/80"
                  }`}
                  data-testid={`section-pill-${s.id}`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {s.label}
                </button>
              );
            })}
            {/* Verify ID — a global, per-agent action (not tied to any one
                property), surfaced right next to Selling/Buying rather than
                buried in a per-property actions grid. Flashes (urgent pulse)
                when the agent hasn't passed yet AND their last attempt (if
                any) scored under 60% — a close call in the 60-69% band gets
                a calmer static amber instead, since nagging about a near-miss
                is less useful than for someone who hasn't tried / failed badly. */}
            {(siteStylesData?.ctaVerifyIdEnabled !== false) && (() => {
              const notVerified = agentInfo && agentInfo.id_verified !== true;
              const lastScore = agentInfo?.latest_verification_score;
              const isUrgent = notVerified && (lastScore == null || lastScore < 60);
              return (
                <button
                  onClick={() => setShowGlobalVerifyId(true)}
                  className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${
                    agentInfo && agentInfo.id_verified === true
                      ? "bg-emerald-500 text-white hover:bg-emerald-600"
                      : notVerified
                        ? `text-amber-950 hover:bg-amber-200 ${isUrgent ? "animate-urgent-pulse" : "bg-amber-100"}`
                        : "bg-muted text-muted-foreground hover:bg-muted/80"
                  }`}
                  data-testid="section-pill-verify-id"
                >
                  <ShieldCheck className="h-3.5 w-3.5" />
                  Verify ID
                </button>
              );
            })()}
            {/* My Account sits on the same row as the section pills so agents
                reach personal settings without hunting through the sub-tab
                bar. `ml-auto` pushes it to the right edge. */}
            <button
              onClick={() => {
                setActiveSection("account");
                setActiveTab("account");
              }}
              className={`ml-auto inline-flex items-center justify-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                activeSection === "account"
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              }`}
              data-testid="section-pill-account"
              title="My Account"
              aria-label="My Account"
            >
              <User className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        {activeSection !== "account" && activeSection !== "house-prices" && activeSection !== "planning-applications" && activeSection !== "planning-appeals" && activeSection !== "plans" && (
        <div className="max-w-5xl mx-auto px-6 border-t border-border">
          <div className="flex gap-0 overflow-x-auto">
            {activeSection === "selling" && (
              <>
            <button
              onClick={() => setActiveTab("my-properties")}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === "my-properties"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
              data-testid="my-properties-tab"
            >
              <Building2 className="inline-block mr-2 h-4 w-4" />
              {linkedProperties.length === 1 ? "My Home" : "My Homes"}
              {linkedProperties.length > 0 && (
                <span className="ml-2 bg-muted text-foreground text-xs px-2 py-0.5 rounded-full">
                  {linkedProperties.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab("statistics")}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "statistics"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
              data-testid="statistics-tab"
            >
              <BarChart3 className="inline-block mr-2 h-4 w-4" />
              Analytics
            </button>
              </>
            )}
            {activeSection === "buying" && (
              <>
                <button
                  onClick={() => setActiveTab("favourites")}
                  className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === "favourites"
                      ? "border-primary text-primary"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                  data-testid="favourites-tab"
                >
                  <Heart className="inline-block mr-2 h-4 w-4" />
                  Favourites
                </button>
                {loginType === "agent" && (
                  <>
                    <button
                      onClick={() => setActiveTab("offers")}
                      className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                        activeTab === "offers"
                          ? "border-primary text-primary"
                          : "border-transparent text-muted-foreground hover:text-foreground"
                      }`}
                      data-testid="offers-tab"
                    >
                      <HandCoins className="inline-block mr-2 h-4 w-4" />
                      Offers made
                    </button>
                    <button
                      onClick={() => setActiveTab("sent-enquiries")}
                      className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                        activeTab === "sent-enquiries"
                          ? "border-primary text-primary"
                          : "border-transparent text-muted-foreground hover:text-foreground"
                      }`}
                      data-testid="sent-enquiries-tab"
                    >
                      <Send className="inline-block mr-2 h-4 w-4" />
                      Enquiry History
                      {sentEnquiries.length > 0 && (
                        <span className="ml-2 bg-muted text-foreground text-xs px-2 py-0.5 rounded-full">
                          {sentEnquiries.length}
                        </span>
                      )}
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
        )}
      </header>

      {/* Global "Verify ID" dialog — agent-level, not tied to any property */}
      <Dialog open={showGlobalVerifyId} onOpenChange={setShowGlobalVerifyId}>
        <DialogContent className="w-[95vw] max-w-[95vw] sm:w-[90vw] sm:max-w-[90vw] max-h-[85vh] overflow-y-auto rounded-none" data-testid="property-modal-verify-id-global">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5" /> Verify ID
            </DialogTitle>
            <DialogDescription>
              Verify your identity once — it applies across all of your properties.
            </DialogDescription>
          </DialogHeader>
          <VerifyIdSection agentId={agentInfo?.agent_id} />
        </DialogContent>
      </Dialog>

      {/* Content */}
      <div className={`mx-auto px-6 py-8 ${activeTab === "my-properties" ? "max-w-7xl" : "max-w-5xl"}`}>
        {/* My Properties List View */}
        {activeTab === "my-properties" && (
          <>
            {siteStylesData?.enforceIdVerification === true && agentInfo && agentInfo.id_verified !== true && (
              <div
                className="mb-4 flex items-center justify-between gap-4 flex-wrap border border-amber-300 bg-amber-50 px-4 py-3"
                data-testid="id-verification-reminder-banner"
              >
                <div className="flex items-center gap-2 text-amber-900">
                  <ShieldAlert className="h-4 w-4 flex-shrink-0" />
                  <p className="text-sm">
                    Please verify your ID to continue using all features.
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-none border-amber-400 text-amber-900 hover:bg-amber-100 animate-urgent-pulse"
                  onClick={() => setShowGlobalVerifyId(true)}
                  data-testid="id-verification-reminder-verify-btn"
                >
                  Verify Now
                </Button>
              </div>
            )}
            <PropertyCardsGrid
              linkedProperties={linkedProperties}
              propertyData={propertyData}
              receivedOffers={receivedOffers}
              milestonesSummary={milestonesSummary}
              switchingProperty={switchingProperty}
              setActiveTab={setActiveTab}
              handleSwitchProperty={handleSwitchProperty}
              openPropertyModal={openPropertyModal}
              setShowNewPropertyDialog={setShowNewPropertyDialog}
            />
            {propertyData?.property_id && (
              <div id="edit-home-details-section" className="mt-6 scroll-mt-40">
                <PropertyEditDialog
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  propertyData={propertyData}
                  setPropertyData={setPropertyData}
                  handleChange={handleChange}
                  handleNumberChange={handleNumberChange}
                  vis={vis}
                  setHasUnsavedChanges={setHasUnsavedChanges}
                  setShowNewPropertyDialog={setShowNewPropertyDialog}
                  setShowScrapeDialog={setShowScrapeDialog}
                  loginType={loginType}
                  aiRewriting={aiRewriting}
                  handleAIRewrite={handleAIRewrite}
                  handleGalleryUpload={handleGalleryUpload}
                  handleDeleteProperty={handleDeleteProperty}
                  deletingProperty={deletingProperty}
                  onOpenEnquiries={() => openEditPortalSubModal("enquiries")}
                  onOpenOrders={() => openEditPortalSubModal("orders")}
                  onOpenOffersReceived={() => openEditPortalSubModal("offers-received")}
                  onOpenDocuments={() => openEditPortalSubModal("documents")}
                  onOpenInfo={() => openEditPortalSubModal("info")}
                  onOpenBuyProducts={() => openEditPortalSubModal("buy-products")}
                />
              </div>
            )}
          </>
        )}
        {/* Sub-modal CTAs opened from Edit Home Details (Enquiries/Orders/Info/
            Offers Received/Documents/Buy Products) render here, gated by
            `subModalTab` instead of `activeTab`, so the My Properties page
            above keeps rendering underneath instead of being replaced. */}
        {(activeTab !== "my-properties" || subModalTab) && (
          <>
        {/* Tab Content */}
        {subModalTab === "enquiries" ? (
          (() => { const enquiriesContent = (
          /* Enquiries Tab */
          <div className="space-y-6">
            {/* Header with Add button */}
            <div className="bg-white border border-border p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-serif text-xl font-medium">Customer Enquiries</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Manage enquiries for this property
                  </p>
                </div>
                <Button
                  onClick={() => setShowAddEnquiryDialog(true)}
                  className="bg-primary text-white hover:bg-primary/90 rounded-none"
                  data-testid="add-enquiry-btn"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add Enquiry
                </Button>
              </div>

              {/* Enquiries List */}
              {loadingEnquiries ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : enquiries.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Users className="mx-auto h-12 w-12 mb-4 opacity-50" />
                  <p>No enquiries yet for this home</p>
                  <p className="text-sm mt-1">Add a manual enquiry or wait for customers to enquire online</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {enquiries.map((enquiry) => (
                    <div 
                      key={enquiry.enquiry_id} 
                      className="border border-border p-4 hover:bg-muted/20 transition-colors"
                      data-testid={`enquiry-card-${enquiry.enquiry_id}`}
                    >
                      {/* Enquiry Header with Name and Contact */}
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                          <User className="h-5 w-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium">{enquiry.name}</h4>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              {enquiry.email}
                            </span>
                            {enquiry.phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="h-3 w-3" />
                                {enquiry.phone}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Message */}
                      {enquiry.message && (
                        <p className="text-sm text-muted-foreground mb-3 pl-13 border-l-2 border-muted pl-3 ml-13">
                          {enquiry.message}
                        </p>
                      )}
                      
                      {/* Date and Source */}
                      <div className="flex items-center gap-4 mb-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatEnquiryDate(enquiry.created_at)}
                        </span>
                        {enquiry.source && (
                          <span className="bg-muted px-2 py-0.5 rounded">
                            {enquiry.source === "manual" ? "Added manually" : "Website enquiry"}
                          </span>
                        )}
                        {enquiry.replied_at && (
                          <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded inline-flex items-center gap-1" data-testid={`enquiry-replied-badge-${enquiry.enquiry_id}`}>
                            <Check className="h-3 w-3" />
                            Replied {new Date(enquiry.replied_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                      
                      {/* Actions - All in one row within the card */}
                      <div className="flex items-center gap-2 pt-3 border-t border-border">
                        <Button
                          size="sm"
                          onClick={() => openReplyDialog(enquiry)}
                          className="bg-primary text-white hover:bg-primary/90 rounded-none"
                          data-testid={`reply-enquiry-${enquiry.enquiry_id}`}
                          title={`Send a templated reply to ${enquiry.email} and mark as replied`}
                        >
                          <Send className="mr-1 h-4 w-4" />
                          {enquiry.replied_at ? "Reply again" : "Reply"}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleSendBrochure(enquiry)}
                          disabled={sendingBrochure === enquiry.enquiry_id}
                          className="rounded-none"
                          data-testid={`send-brochure-${enquiry.enquiry_id}`}
                          title={`Email brochure to ${enquiry.email}`}
                        >
                          {sendingBrochure === enquiry.enquiry_id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <Download className="mr-1 h-4 w-4" />
                              Send Brochure
                            </>
                          )}
                        </Button>
                        <a 
                          href={`mailto:${enquiry.email}`}
                          className="inline-flex items-center justify-center h-9 px-3 border border-border hover:bg-muted transition-colors"
                          title="Send email"
                        >
                          <Mail className="h-4 w-4" />
                        </a>
                        {enquiry.phone && (
                          <a 
                            href={`tel:${enquiry.phone}`}
                            className="inline-flex items-center justify-center h-9 px-3 border border-border hover:bg-muted transition-colors"
                            title="Call"
                          >
                            <Phone className="h-4 w-4" />
                          </a>
                        )}
                        
                        {/* Spacer to push edit/delete to the right */}
                        <div className="flex-1" />
                        
                        <button
                          onClick={() => handleEditEnquiry(enquiry)}
                          className="inline-flex items-center justify-center h-9 px-3 border border-border hover:bg-muted transition-colors"
                          title="Edit enquiry"
                          data-testid={`edit-enquiry-${enquiry.enquiry_id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(enquiry.enquiry_id)}
                          className="inline-flex items-center justify-center h-9 px-3 border border-destructive/50 text-destructive hover:bg-destructive/10 transition-colors"
                          title="Delete enquiry"
                          data-testid={`delete-enquiry-${enquiry.enquiry_id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      
                      {/* Delete Confirmation */}
                      {showDeleteConfirm === enquiry.enquiry_id && (
                        <div className="mt-4 p-3 bg-destructive/10 border border-destructive/30 flex items-center justify-between">
                          <span className="text-sm text-destructive">Are you sure you want to delete this enquiry?</span>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setShowDeleteConfirm(null)}
                              className="rounded-none"
                            >
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleDeleteEnquiry(enquiry.enquiry_id)}
                              disabled={deletingEnquiry === enquiry.enquiry_id}
                              className="bg-destructive text-white hover:bg-destructive/90 rounded-none"
                            >
                              {deletingEnquiry === enquiry.enquiry_id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                "Delete"
                              )}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          ); return cardModalMode ? (
            <Dialog open onOpenChange={(o) => { if (!o) closeCardModal(); }}>
              <DialogContent className="slh-dialog-fixed-90 w-[95vw] max-w-[95vw] sm:w-[90vw] sm:max-w-[90vw] max-h-[85vh] overflow-y-auto rounded-none" data-testid="property-modal-enquiries">
                <DialogHeader>
                  <DialogTitle>Enquiries — {propertyData?.title || "Home"}</DialogTitle>
                </DialogHeader>
                {enquiriesContent}
              </DialogContent>
            </Dialog>
          ) : enquiriesContent; })()
        ) : activeTab === "statistics" ? (
          <StatisticsTab enquiries={enquiries} />
        ) : activeTab === "account" ? (
          <AgentAccountPanel
            agentInfo={agentInfo}
            username={username}
            profileForm={profileForm}
            onFieldChange={handleProfileFieldChange}
            onSave={handleSaveProfile}
            saving={profileSaving}
            hasChanges={profileHasChanges}
            lastSaved={profileLastSaved}
          />
        ) : activeTab === "house-prices" ? (
          <HousePricesTool />
        ) : activeTab === "planning-applications" ? (
          <PlanningApplicationsTool linkedProperties={linkedProperties} />
        ) : activeTab === "planning-appeals" ? (
          <PlanningAppealsTool />
        ) : activeTab === "plans" ? (
          <PlansSection />
        ) : subModalTab === "orders" ? (
          cardModalMode ? (
            <Dialog open onOpenChange={(o) => { if (!o) closeCardModal(); }}>
              <DialogContent className="slh-dialog-content-expand w-[95vw] max-w-[95vw] sm:w-[90vw] sm:max-w-[90vw] max-h-[85vh] overflow-y-auto rounded-none" data-testid="property-modal-orders">
                <DialogHeader>
                  <DialogTitle>Orders — {propertyData?.title || "Home"}</DialogTitle>
                </DialogHeader>
                <AgentOrdersTab
                  orders={orders}
                  ordersLoading={ordersLoading}
                  linkedProperties={linkedProperties}
                  setOrders={setOrders}
                  filterPropertyId={cardModalPropertyId}
                />
              </DialogContent>
            </Dialog>
          ) : (
          <AgentOrdersTab
            orders={orders}
            ordersLoading={ordersLoading}
            linkedProperties={linkedProperties}
            setOrders={setOrders}
          />
          )
        ) : activeTab === "favourites" ? (
          <PropertyAdminFavouritesTab linkedProperties={linkedProperties} />
        ) : activeTab === "offers" ? (
          <PropertyAdminOffersTab linkedProperties={linkedProperties} />
        ) : subModalTab === "info" ? (
          cardModalMode ? (
            <Dialog open onOpenChange={(o) => { if (!o) closeCardModal(); }}>
              <DialogContent className="slh-dialog-content-expand w-[95vw] max-w-[95vw] sm:w-[90vw] sm:max-w-[90vw] max-h-[85vh] overflow-y-auto rounded-none" data-testid="property-modal-info">
                <DialogHeader>
                  <DialogTitle>Info — {propertyData?.title || "Home"}</DialogTitle>
                </DialogHeader>
                <PropertyAdminInfoTab linkedProperties={linkedProperties} forcedPropertyId={cardModalPropertyId} />
              </DialogContent>
            </Dialog>
          ) : (
          <PropertyAdminInfoTab linkedProperties={linkedProperties} />
          )
        ) : subModalTab === "offers-received" ? (
          cardModalMode ? (
            <Dialog open onOpenChange={(o) => { if (!o) closeCardModal(); }}>
              <DialogContent className="slh-dialog-fixed-90 w-[95vw] max-w-[95vw] sm:w-[90vw] sm:max-w-[90vw] max-h-[85vh] overflow-y-auto rounded-none" data-testid="property-modal-offers-received">
                <DialogHeader>
                  <DialogTitle>Offers Received — {propertyData?.title || "Home"}</DialogTitle>
                </DialogHeader>
                <PropertyAdminOffersReceivedTab
                  focusPropertyId={cardModalPropertyId}
                  onClearFocus={() => {}}
                  onDataChange={refreshReceivedOffers}
                />
              </DialogContent>
            </Dialog>
          ) : (
          <PropertyAdminOffersReceivedTab
            focusPropertyId={focusOffersPropertyId}
            onClearFocus={() => setFocusOffersPropertyId(null)}
            onDataChange={refreshReceivedOffers}
          />
          )
        ) : subModalTab === "documents" ? (
          cardModalMode ? (
            <Dialog open onOpenChange={(o) => { if (!o) closeCardModal(); }}>
              <DialogContent className="slh-dialog-fixed-90 w-[95vw] max-w-[95vw] sm:w-[90vw] sm:max-w-[90vw] max-h-[85vh] overflow-y-auto rounded-none" data-testid="property-modal-documents">
                <DialogHeader>
                  <DialogTitle>Documents — {propertyData?.title || "Home"}</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-muted-foreground -mt-2">
                  Files uploaded here are filed against this property only.
                </p>
                <DocumentsSection propertyId={cardModalPropertyId} />
              </DialogContent>
            </Dialog>
          ) : (
          <div className="bg-white border border-border p-6" data-testid="documents-tab-content">
            <div className="mb-4">
              <h2 className="text-xl font-semibold flex items-center gap-2 mb-1">
                <FolderOpen className="h-5 w-5" /> Documents
              </h2>
              <p className="text-sm text-muted-foreground">
                Your private document library. Organise files into nested folders, share them via a public link or email, and protect important folders from accidental deletion.
              </p>
            </div>
            <DocumentsSection />
          </div>
          )
        ) : activeTab === "verify-id" ? (
          <Dialog open onOpenChange={(o) => { if (!o) closeCardModal(); }}>
            <DialogContent className="w-[95vw] max-w-[95vw] sm:w-[90vw] sm:max-w-[90vw] max-h-[85vh] overflow-y-auto rounded-none" data-testid="property-modal-verify-id">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5" /> Verify ID — {propertyData?.title || "Home"}
                </DialogTitle>
              </DialogHeader>
              <VerifyIdSection propertyId={cardModalPropertyId} />
            </DialogContent>
          </Dialog>
        ) : subModalTab === "buy-products" ? (
          <Dialog open onOpenChange={(o) => { if (!o) closeCardModal(); }}>
            <DialogContent className="slh-dialog-content-expand w-[95vw] max-w-[95vw] sm:w-[90vw] sm:max-w-[90vw] max-h-[85vh] overflow-y-auto rounded-none" data-testid="property-modal-buy-products">
              <DialogHeader>
                <DialogTitle>Buy Products — {propertyData?.title || "Home"}</DialogTitle>
              </DialogHeader>
              <BuyProductsTab propertyId={cardModalPropertyId} />
            </DialogContent>
          </Dialog>
        ) : activeTab === "sent-enquiries" ? (
          <div className="bg-white border border-border p-6" data-testid="sent-enquiries-tab-content">
            <h2 className="text-xl font-semibold flex items-center gap-2 mb-1">
              <Send className="h-5 w-5" /> Enquiry History
            </h2>
            <p className="text-sm text-muted-foreground mb-6">
              Every enquiry you&apos;ve sent to another customer&apos;s property page. Sent means delivered; Viewed means the customer has opened their inbox; Replied means they&apos;ve flagged your enquiry as answered.
            </p>
            {sentEnquiriesLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : sentEnquiries.length === 0 ? (
              <div className="border border-dashed border-border rounded-lg p-8 text-center text-sm text-muted-foreground bg-muted/20" data-testid="sent-enquiries-empty">
                You haven&apos;t sent any enquiries yet.
                {" "}
                <Link to="/properties" className="text-primary hover:underline font-medium">Browse homes →</Link>
              </div>
            ) : (
              <div className="border border-border rounded overflow-x-auto" data-testid="sent-enquiries-list">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide">
                    <tr>
                      <th className="p-3">Sent</th>
                      <th className="p-3">Home</th>
                      <th className="p-3">Customer</th>
                      <th className="p-3">Message</th>
                      <th className="p-3">Status</th>
                      <th className="p-3 text-right"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sentEnquiries.map((e) => {
                      const status = e.status || "sent";
                      const badgeCls = status === "replied"
                        ? "bg-green-100 text-green-700"
                        : status === "viewed"
                          ? "bg-blue-100 text-blue-700"
                          : "bg-amber-100 text-amber-700";
                      return (
                        <tr key={e.enquiry_id} className="border-t border-border align-top" data-testid={`sent-enquiry-row-${e.enquiry_id}`}>
                          <td className="p-3 text-xs whitespace-nowrap">{e.created_at ? new Date(e.created_at).toLocaleString() : "—"}</td>
                          <td className="p-3">
                            <div className="font-medium">{e.property_title || "—"}</div>
                            {e.property_id && (
                              <Link to={`/property/${e.property_id}`} className="text-xs text-primary hover:underline">
                                View property →
                              </Link>
                            )}
                          </td>
                          <td className="p-3 text-xs">{e.agent_name || "—"}</td>
                          <td className="p-3 text-xs max-w-xs whitespace-pre-wrap">{e.message || "—"}</td>
                          <td className="p-3">
                            <span className={`text-xs px-2 py-0.5 rounded ${badgeCls} capitalize`} data-testid={`sent-enquiry-status-${e.enquiry_id}`}>{status}</span>
                            {status === "viewed" && e.viewed_at && (
                              <div className="text-[10px] text-muted-foreground mt-1">on {new Date(e.viewed_at).toLocaleDateString()}</div>
                            )}
                            {status === "replied" && e.replied_at && (
                              <div className="text-[10px] text-muted-foreground mt-1">on {new Date(e.replied_at).toLocaleDateString()}</div>
                            )}
                          </td>
                          <td className="p-3 text-right"></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : null}
        </>
        )}
      </div>


      {/* Inline Reply Dialog — sends a templated email via Resend AND marks
          the enquiry as replied so the sender's Buying Enquiry History flips
          to "Replied" in one round trip. */}
      <Dialog
        open={replyDialog.open}
        onOpenChange={(open) => {
          if (!open && !replyDialog.sending) setReplyDialog({ open: false, enquiry: null, subject: "", message: "", sending: false });
        }}
      >
        <DialogContent className="sm:max-w-lg" data-testid="reply-enquiry-dialog">
          <DialogHeader>
            <DialogTitle>Reply to {replyDialog.enquiry?.name || "enquiry"}</DialogTitle>
            <DialogDescription>
              Send a templated reply email and mark this enquiry as replied. The message will go to <span className="font-medium">{replyDialog.enquiry?.email}</span>{agentInfo?.email ? <> — replies land back in <span className="font-medium">{agentInfo.email}</span></> : null}.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Subject</Label>
              <Input
                value={replyDialog.subject}
                onChange={(e) => setReplyDialog((d) => ({ ...d, subject: e.target.value }))}
                className="rounded-none"
                data-testid="reply-subject-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Message</Label>
              <Textarea
                value={replyDialog.message}
                onChange={(e) => setReplyDialog((d) => ({ ...d, message: e.target.value }))}
                className="rounded-none min-h-40"
                data-testid="reply-message-input"
              />
              <p className="text-xs text-muted-foreground">
                Tweak the template as needed. Line breaks are preserved in the email.
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setReplyDialog({ open: false, enquiry: null, subject: "", message: "", sending: false })}
              disabled={replyDialog.sending}
              className="rounded-none"
              data-testid="reply-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSendReply}
              disabled={replyDialog.sending || !replyDialog.message?.trim()}
              className="bg-primary text-white hover:bg-primary/90 rounded-none"
              data-testid="reply-send-btn"
            >
              {replyDialog.sending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" /> Sending
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" /> Send &amp; mark replied
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>


      {/* Add Enquiry Dialog */}
      <Dialog open={showAddEnquiryDialog} onOpenChange={setShowAddEnquiryDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Customer Enquiry</DialogTitle>
            <DialogDescription>
              Manually add a customer enquiry for this property
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Customer Name *</Label>
              <Input
                value={newEnquiry.name}
                onChange={(e) => setNewEnquiry(prev => ({ ...prev, name: e.target.value }))}
                placeholder="John Smith"
                className="rounded-none"
                data-testid="new-enquiry-name"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Email Address *</Label>
              <Input
                type="email"
                value={newEnquiry.email}
                onChange={(e) => setNewEnquiry(prev => ({ ...prev, email: e.target.value }))}
                placeholder="john@example.com"
                className="rounded-none"
                data-testid="new-enquiry-email"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Telephone</Label>
              <Input
                type="tel"
                value={newEnquiry.phone}
                onChange={(e) => setNewEnquiry(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+44 7123 456789"
                className="rounded-none"
                data-testid="new-enquiry-phone"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Notes / Message</Label>
              <Textarea
                value={newEnquiry.message}
                onChange={(e) => setNewEnquiry(prev => ({ ...prev, message: e.target.value }))}
                placeholder="Any additional notes about this enquiry..."
                className="rounded-none min-h-20"
                data-testid="new-enquiry-message"
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setShowAddEnquiryDialog(false)}
              className="rounded-none"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddEnquiry}
              disabled={addingEnquiry || !newEnquiry.name || !newEnquiry.email}
              className="bg-primary text-white hover:bg-primary/90 rounded-none"
              data-testid="submit-enquiry-btn"
            >
              {addingEnquiry ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Enquiry
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Enquiry Dialog */}
      <Dialog open={showEditEnquiryDialog} onOpenChange={setShowEditEnquiryDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Customer Enquiry</DialogTitle>
            <DialogDescription>
              Update customer enquiry details
            </DialogDescription>
          </DialogHeader>
          
          {editingEnquiry && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Customer Name *</Label>
                <Input
                  value={editingEnquiry.name}
                  onChange={(e) => setEditingEnquiry(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="John Smith"
                  className="rounded-none"
                  data-testid="edit-enquiry-name"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Email Address *</Label>
                <Input
                  type="email"
                  value={editingEnquiry.email}
                  onChange={(e) => setEditingEnquiry(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="john@example.com"
                  className="rounded-none"
                  data-testid="edit-enquiry-email"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Telephone</Label>
                <Input
                  type="tel"
                  value={editingEnquiry.phone || ""}
                  onChange={(e) => setEditingEnquiry(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="+44 7123 456789"
                  className="rounded-none"
                  data-testid="edit-enquiry-phone"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Notes / Message</Label>
                <Textarea
                  value={editingEnquiry.message || ""}
                  onChange={(e) => setEditingEnquiry(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Any additional notes about this enquiry..."
                  className="rounded-none min-h-20"
                  data-testid="edit-enquiry-message"
                />
              </div>
            </div>
          )}
          
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowEditEnquiryDialog(false);
                setEditingEnquiry(null);
              }}
              className="rounded-none"
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateEnquiry}
              disabled={updatingEnquiry || !editingEnquiry?.name || !editingEnquiry?.email}
              className="bg-primary text-white hover:bg-primary/90 rounded-none"
              data-testid="update-enquiry-btn"
            >
              {updatingEnquiry ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import URL Dialog */}
      <Dialog open={showScrapeDialog} onOpenChange={setShowScrapeDialog}>
        <DialogContent className="sm:max-w-lg rounded-none">
          <DialogHeader>
            <DialogTitle className="font-serif">Import from URL</DialogTitle>
            <DialogDescription>
              Paste a property listing URL (e.g. Rightmove, Zoopla) to import details automatically
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex gap-2">
              <Input
                value={scrapeUrl}
                onChange={(e) => setScrapeUrl(e.target.value)}
                placeholder="https://www.rightmove.co.uk/properties/..."
                className="rounded-none flex-1"
                data-testid="prop-admin-scrape-input"
              />
              <Button
                onClick={handleScrape}
                disabled={scraping || !scrapeUrl}
                className="rounded-none"
                data-testid="prop-admin-scrape-btn"
              >
                {scraping ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4 mr-1" />}
                {scraping ? "Importing..." : "Import"}
              </Button>
            </div>

            {scrapeResult && (
              <div className="border border-border p-4 space-y-2 bg-muted/30">
                <h4 className="font-medium text-sm">Imported Data Preview</h4>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {scrapeResult.title && <div><span className="text-muted-foreground">Title:</span> {scrapeResult.title}</div>}
                  {scrapeResult.price && <div><span className="text-muted-foreground">Price:</span> {scrapeResult.price}</div>}
                  {scrapeResult.beds && <div><span className="text-muted-foreground">Beds:</span> {scrapeResult.beds}</div>}
                  {scrapeResult.baths && <div><span className="text-muted-foreground">Baths:</span> {scrapeResult.baths}</div>}
                  {scrapeResult.property_type && <div><span className="text-muted-foreground">Type:</span> {scrapeResult.property_type}</div>}
                  {scrapeResult.images?.length > 0 && <div><span className="text-muted-foreground">Images:</span> {scrapeResult.images.length}</div>}
                </div>
                {scrapeResult.description && (
                  <div className="text-xs mt-2">
                    <span className="text-muted-foreground">Description:</span>
                    <p className="mt-1 text-muted-foreground line-clamp-3">{scrapeResult.description}</p>
                    <p className="text-[10px] text-green-600 mt-1">{scrapeResult.description.length} characters captured</p>
                  </div>
                )}
                <Button onClick={applyScrapeData} size="sm" className="rounded-none w-full mt-2">
                  Apply to Property
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add New Property Dialog */}
      <Dialog open={showNewPropertyDialog} onOpenChange={setShowNewPropertyDialog}>
        <DialogContent className="sm:max-w-lg rounded-none" data-testid="new-property-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Add New Home</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Create a new listing — it will be saved as a draft (not live) and added to your homes.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs">Home Title *</Label>
              <Input
                value={newPropertyForm.title}
                onChange={e => setNewPropertyForm(p => ({ ...p, title: e.target.value }))}
                placeholder="e.g. 3 Bedroom Semi-Detached in Manchester"
                className="rounded-none mt-1"
                data-testid="new-property-title"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Price</Label>
                <div className="flex mt-1">
                  <select
                    value={newPropertyForm.currency}
                    onChange={e => setNewPropertyForm(p => ({ ...p, currency: e.target.value }))}
                    className="h-10 px-2 border border-r-0 border-border bg-muted/50 text-sm focus:outline-none"
                  >
                    <option value="£">£</option>
                    <option value="$">$</option>
                    <option value="€">€</option>
                  </select>
                  <Input
                    type="number"
                    value={newPropertyForm.price}
                    onChange={e => setNewPropertyForm(p => ({ ...p, price: e.target.value }))}
                    placeholder="250000"
                    className="rounded-none"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Display Address</Label>
                <Input
                  value={newPropertyForm.display_address}
                  onChange={e => setNewPropertyForm(p => ({ ...p, display_address: e.target.value }))}
                  placeholder="City, Area"
                  className="rounded-none mt-1"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Beds</Label>
                <Input type="number" min="0" value={newPropertyForm.beds} onChange={e => setNewPropertyForm(p => ({ ...p, beds: e.target.value }))} className="rounded-none mt-1" placeholder="3" />
              </div>
              <div>
                <Label className="text-xs">Baths</Label>
                <Input type="number" min="0" value={newPropertyForm.baths} onChange={e => setNewPropertyForm(p => ({ ...p, baths: e.target.value }))} className="rounded-none mt-1" placeholder="2" />
              </div>
              <div>
                <Label className="text-xs">Type</Label>
                <select
                  value={newPropertyForm.property_type}
                  onChange={e => setNewPropertyForm(p => ({ ...p, property_type: e.target.value }))}
                  className="w-full h-10 mt-1 px-2 border border-border bg-background text-sm focus:outline-none rounded-none"
                >
                  <option value="">Select...</option>
                  <option value="terraced">Terraced</option>
                  <option value="semi-detached">Semi-Detached</option>
                  <option value="detached">Detached</option>
                  <option value="flat">Flat</option>
                  <option value="bungalow">Bungalow</option>
                  <option value="townhouse">Townhouse</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                className="flex-1 rounded-none"
                onClick={() => setShowNewPropertyDialog(false)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 rounded-none"
                disabled={!newPropertyForm.title.trim() || newPropertySaving}
                data-testid="create-property-btn"
                onClick={async () => {
                  setNewPropertySaving(true);
                  try {
                    const res = await axios.post(`${API}/property-admin/new-property`, {
                      title: newPropertyForm.title,
                      price: Number(newPropertyForm.price) || 0,
                      currency: newPropertyForm.currency,
                      display_address: newPropertyForm.display_address,
                      beds: Number(newPropertyForm.beds) || 0,
                      baths: Number(newPropertyForm.baths) || 0,
                      property_type: newPropertyForm.property_type,
                      tenure: newPropertyForm.tenure,
                    }, { withCredentials: true });
                    toast.success(`"${res.data.title}" created — added to your list`);
                    setShowNewPropertyDialog(false);
                    setNewPropertyForm({ title: "", price: "", currency: "£", location: "", beds: "", baths: "", property_type: "", tenure: "" });
                    // Refresh linked properties list and show the list page
                    await fetchPropertyData();
                    setActiveTab("my-properties");
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Failed to create property");
                  } finally {
                    setNewPropertySaving(false);
                  }
                }}
              >
                {newPropertySaving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating…</> : "Create Property"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
